// /admin/v2/points — Point editor (BRIEFS C4.4): pick a chapter → a unit → a point (or type
// a point code), edit its html with a live preview, save. The old wording goes to the
// history first; Harsh's highlights whose words are gone are marked for checking.
// ?chapter=<id>&code=<point code> opens a point directly.
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  v2AdminChapters, v2ChapterNotesPreview, v2GetPoint, v2SavePoint, v2PreviewPoint, errText,
} from '../../../api/v2admin';
import V2Tabs, { Msg, chapterLabel, NOTES_CSS } from './V2Tabs';

function unitLabel(u) {
  return [u.number, u.title].filter(Boolean).join(' ') || u.code;
}

function depthOf(points) {
  const depth = {};
  points.forEach((p) => { depth[p.code] = p.parent ? (depth[p.parent] || 0) + 1 : 0; });
  return depth;
}

function Editor({ code, onSaved, onLoaded }) {
  const qc = useQueryClient();
  const { data: pt, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['v2-admin-point', code],
    queryFn: () => v2GetPoint(code),
    enabled: !!code,
  });
  const [html, setHtml] = useState('');
  const [source, setSource] = useState('ncert');
  const [page, setPage] = useState('');
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!pt) return;
    setHtml(pt.html); setSource(pt.source); setPage(pt.page == null ? '' : String(pt.page));
    setPreview(pt.html);
    if (onLoaded) onLoaded(pt);
  }, [pt]);
  useEffect(() => { setMsg(null); }, [code]);

  // live preview: what the server would save (sanitised), 300 ms after typing stops
  useEffect(() => {
    if (!pt) return undefined;
    const t = setTimeout(() => {
      v2PreviewPoint(html).then((r) => setPreview(r.html)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [html, pt]);

  if (!code) return <div className="card" style={{ color: 'var(--text-muted)' }}>Choose a point on the left, or type its code above.</div>;
  if (isLoading) return <div className="card"><div className="spinner" /></div>;
  if (isError) return <div className="card" style={{ color: 'var(--danger)' }}>{errText(error, 'Could not load this point.')}</div>;

  const pageNum = page.trim() === '' ? null : Number(page);
  const pageBad = page.trim() !== '' && (!Number.isInteger(pageNum) || pageNum < 1);
  const dirty = html !== pt.html || source !== pt.source || pageNum !== pt.page;

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await v2SavePoint(code, { html, source, page: pageNum });
      let text = r.changed ? `Saved. ${code} is now version ${r.version}.` : 'Nothing changed.';
      if (r.highlights_flagged) text += ` ${r.highlights_flagged} highlight(s) of a student now need checking.`;
      setMsg({ kind: 'ok', text });
      qc.setQueryData(['v2-admin-point', code], r);
      onSaved && onSaved();
      refetch();
    } catch (e) {
      setMsg({ kind: 'err', text: errText(e, 'Could not save.') });
    } finally { setBusy(false); }
  };

  return (
    <div className="card" data-testid="v2-point-editor" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{pt.unit.code} · {[pt.unit.number, pt.unit.title].filter(Boolean).join(' ')}</div>
        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 18 }}>{pt.code}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          version {pt.version}{pt.is_removed ? ' · HIDDEN (removed from the notes)' : ''} · {pt.highlights} student highlight(s) on this point
        </div>
      </div>
      <Msg msg={msg} />
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <label className="form-label" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          Source
          <select className="form-select" value={source} onChange={(e) => setSource(e.target.value)} style={{ width: 160 }}>
            <option value="ncert">NCERT</option>
            <option value="extra">Extra</option>
          </select>
        </label>
        <label className="form-label" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          NCERT page
          <input className="form-input" value={page} onChange={(e) => setPage(e.target.value)} style={{ width: 120 }}
            placeholder="none" inputMode="numeric" />
        </label>
      </div>
      {pageBad && <div style={{ color: 'var(--danger)', fontSize: 13 }}>The page must be a number.</div>}
      <label className="form-label" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        Text (HTML). Colours: &lt;span class="c-red"&gt; must-learn, "c-blue" names, "c-green" examples, "c-mark" exam favourite.
        <textarea className="form-textarea" value={html} onChange={(e) => setHtml(e.target.value)} rows={6}
          data-testid="v2-point-html" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }} />
      </label>
      <div>
        <div className="form-label" style={{ marginBottom: 4 }}>Preview (as Harsh will see it)</div>
        <style>{NOTES_CSS}</style>
        <div className="v2n" data-testid="v2-point-preview" style={{ border: '1px dashed var(--border)', borderRadius: 8, padding: '10px 14px', background: '#FFFFFF' }}
          dangerouslySetInnerHTML={{ __html: preview }} />
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" disabled={!dirty || busy || pageBad} onClick={save} data-testid="v2-point-save">Save</button>
        <button className="btn btn-ghost" disabled={!dirty || busy} onClick={() => { setHtml(pt.html); setSource(pt.source); setPage(pt.page == null ? '' : String(pt.page)); }}>Undo my changes</button>
      </div>
      <p style={{ fontSize: 12, color: '#B45309', margin: 0 }}>
        This changes the notes saved on the site. The chapter's notes file in the code keeps its own words:
        when that file is changed and loaded again, its words come back. Ask Claude to put the same change into the file.
      </p>
      <div>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Earlier wording</div>
        {pt.history.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No earlier wording.</div> : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {pt.history.map((h) => (
              <li key={`${h.version}-${h.changed_at}`} style={{ marginBottom: 6 }}>
                <span style={{ color: 'var(--text-muted)' }}>
                  version {h.version} · replaced {h.changed_at ? new Date(h.changed_at).toLocaleString('en-IN') : ''}{h.changed_by ? ` by ${h.changed_by}` : ' by a notes file'}
                </span>
                <div className="v2n" style={{ fontSize: 14 }} dangerouslySetInnerHTML={{ __html: h.html }} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function AdminV2Points() {
  const [params, setParams] = useSearchParams();
  const chapterId = params.get('chapter') || '';
  const code = params.get('code') || '';
  const [unitCode, setUnitCode] = useState('');
  const [find, setFind] = useState('');

  const { data: chapters } = useQuery({ queryKey: ['v2-admin-chapters'], queryFn: v2AdminChapters });
  const withNotes = (chapters || []).filter((c) => c.points > 0);
  const { data: notes, isLoading, refetch } = useQuery({
    queryKey: ['v2-admin-notes-preview', chapterId],
    queryFn: () => v2ChapterNotesPreview(chapterId),
    enabled: !!chapterId,
  });

  // open the unit of the chosen point (else the first unit)
  useEffect(() => {
    if (!notes) return;
    const u = code ? notes.units.find((x) => x.points.some((p) => p.code === code)) : null;
    if (u) setUnitCode(u.code);
    else setUnitCode((cur) => (cur && notes.units.some((x) => x.code === cur) ? cur : (notes.units[0] ? notes.units[0].code : '')));
  }, [notes, code]);

  const unit = useMemo(() => (notes ? notes.units.find((u) => u.code === unitCode) : null), [notes, unitCode]);
  const depth = useMemo(() => depthOf(unit ? unit.points : []), [unit]);

  const set = (next) => {
    const p = {};
    if (next.chapter) p.chapter = next.chapter;
    if (next.code) p.code = next.code;
    setParams(p);
  };

  const goFind = (e) => {
    e.preventDefault();
    const c = find.trim();
    if (c) set({ chapter: chapterId, code: c });
  };

  return (
    <div>
      <V2Tabs title="Point editor"
        intro="Change the words of one point. The old words are kept in the history, and Harsh's highlights on words that are gone are marked for checking." />
      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', padding: '14px 18px' }}>
        <select className="form-select" value={chapterId} style={{ maxWidth: 460 }}
          onChange={(e) => { setUnitCode(''); set({ chapter: e.target.value }); }} data-testid="v2-points-chapter">
          <option value="">Choose a chapter…</option>
          {withNotes.map((c) => <option key={c.chapter_id} value={c.chapter_id}>{chapterLabel(c)}</option>)}
        </select>
        <form onSubmit={goFind} style={{ display: 'flex', gap: 8 }}>
          <input className="form-input" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Point code, e.g. XII-05.4.1-P03" style={{ width: 260 }} />
          <button className="btn btn-ghost" type="submit">Open</button>
        </form>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) minmax(260px, 1fr) minmax(320px, 1.3fr)', gap: 16, alignItems: 'start' }}>
        <div className="card" style={{ padding: 10, maxHeight: '75vh', overflowY: 'auto' }}>
          {!chapterId ? <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: 6 }}>Choose a chapter.</div>
            : isLoading ? <div className="spinner" /> : (notes?.units || []).map((u) => (
              <button key={u.code} type="button" onClick={() => setUnitCode(u.code)} style={{
                display: 'block', width: '100%', textAlign: 'left', border: 0, cursor: 'pointer', borderRadius: 6,
                padding: '6px 8px', fontSize: 13, marginBottom: 2,
                paddingLeft: 8 + (u.parent ? 12 : 0) + (u.kind === 'heading' ? 6 : 0),
                fontStyle: u.kind === 'heading' ? 'italic' : 'normal',
                background: u.code === unitCode ? 'var(--primary-light)' : 'transparent',
                color: u.code === unitCode ? 'var(--primary-dark)' : 'var(--text)',
                fontWeight: u.code === unitCode ? 600 : 400,
              }}>{unitLabel(u)} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({u.points.length})</span></button>
            ))}
        </div>
        <div className="card" style={{ padding: 10, maxHeight: '75vh', overflowY: 'auto' }}>
          <style>{NOTES_CSS}</style>
          {!unit ? <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: 6 }}>Choose a unit.</div> : unit.points.map((p) => (
            <button key={p.code} type="button" onClick={() => set({ chapter: chapterId, code: p.code })}
              data-testid={`v2-points-item-${p.code}`} style={{
                display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', borderRadius: 6,
                border: p.code === code ? '1px solid var(--primary)' : '1px solid transparent',
                background: p.code === code ? 'var(--primary-light)' : 'transparent',
                padding: '6px 8px', marginBottom: 2, marginLeft: (depth[p.code] || 0) * 14,
                width: `calc(100% - ${(depth[p.code] || 0) * 14}px)`,
              }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.code}{p.source === 'extra' ? ' · Extra' : ''}</div>
              <div className="v2n" style={{ fontSize: 14 }} dangerouslySetInnerHTML={{ __html: p.html }} />
            </button>
          ))}
        </div>
        <Editor code={code} onSaved={() => refetch()}
          onLoaded={(pt) => { if (String(pt.chapter_id) !== chapterId) set({ chapter: String(pt.chapter_id), code }); }} />
      </div>
    </div>
  );
}
